import { useEffect, useState } from "react"
import styled from "styled-components"

import { messagesApi } from "@/fetch/api/messages"
import { formatTime } from "@/lib/formatTime"
import { Message, MessageAttachment } from "../../../../types"
import {
  DeleteIcon,
  EditIcon,
  IconButton,
  UserIcon,
} from "@/components/button/IconButton"
import { formatAttachmentUrl } from "@/lib/formatAttachmentUrl"
import { EmptyScreen } from "@/components/emptyScreen/EmptyScreen"
import { ConfirmModal } from "@/components/modal/ConfirmModal"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import pdfDataPreview from "./pdfIconDataUri"
import { ViewMessageModal } from "./ViewMessageModal"
import { Button } from "@/components/button/Button"
import React from "react"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-grow: 1;
  overflow-y: auto;
`

const MessageCard = styled.div<{
  $isGrayOut: boolean
  $isActive: boolean
}>`
  ${({ $isGrayOut }) => ($isGrayOut ? "opacity: 0.6;" : "")}
  background: ${({ $isActive }) =>
    $isActive ? "var(--light-iris)" : "#fafcfe"};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
`

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  img {
    width: 24px;
    height: 24px;
  }
`

const DateTime = styled.div`
  font-size: 14px;
  color: #999;
  white-space: nowrap;
`

const MessageActions = styled.div`
  display: flex;
  align-items: center;

  img {
    width: 16px;
    height: 16px;
  }
`

const StyledFileLink = styled.a`
  text-decoration: none;
  gap: 4px;
  color: var(--iris);
  font-size: 12px;
  font-weight: 500;
  &:hover {
    color: var(--iris-hover);
  }
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 120px;
  border: 1px solid var(--accent);
  border-radius: 8px;
  padding: 8px;
  background: #fafcfe;

  img {
    width: 120px;
  }
`

const RecipientListButton = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`
export function extractHrefs(line: string) {
  const urlRegex = /(https?:\/\/[^\s]+)/g

  const parts = line.split(urlRegex)

  return (
    <span>
      {parts.map((part, index) => {
        if (part.match(/^https?:\/\//)) {
          return (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--iris)" }}
            >
              {part}
            </a>
          )
        }

        return <React.Fragment key={index}>{part}</React.Fragment>
      })}
    </span>
  )
}

const MessageContent = ({
  content,
  attachments,
}: {
  content: string
  attachments?: MessageAttachment[]
}) => {
  const lines = content
    .split(/\r\n/g)
    .map((line, i) => <p key={i}>{extractHrefs(line)}</p>)

  function openPreview(url: string) {
    window.open(url, "_blank")
  }
  return (
    <>
      {lines}
      {attachments?.map((attach) => {
        switch (attach.mimeType) {
          case "image/jpeg":
          case "image/png": {
            const url = formatAttachmentUrl(attach.url)
            return (
              <>
                <img src={url} alt="" key={attach.id} />
                <Button variant="inline" onClick={() => openPreview(url)}>
                  Atvērt attēlu pilnā izmērā
                </Button>
              </>
            )
          }
          case "application/pdf":
          case "application/x-pdf": {
            return (
              <StyledFileLink
                href={formatAttachmentUrl(attach.url)}
                download={attach.name}
                target="_blank"
                rel="noopener noreferrer"
                key={attach.id}
                title={attach.name}
              >
                <img src={pdfDataPreview} alt={attach.name} />
                <span>{attach.name}</span>
              </StyledFileLink>
            )
          }
          default: {
            return null
          }
        }
      })}
    </>
  )
}
export const AllNotificationsList = ({
  setEditMessage,
  editMessageId,
}: {
  setEditMessage: (message: Message) => void
  editMessageId?: number
}) => {
  const [messages, setMessages] = useState<Message[]>()
  const [deleteMessageId, setDeleteMessageId] = useState<Message["id"] | null>(
    null,
  )
  const [viewMessage, setViewMessage] = useState<Message | null>(null)

  useEffect(() => {
    const init = async () => {
      try {
        const data = await messagesApi.getMessages()
        if (data) {
          setMessages(data)
        }
      } catch (err) {
        console.error(err)
      }
    }
    init()
  }, [])

  const deleteMessage = async () => {
    try {
      if (deleteMessageId) {
        await messagesApi.deleteMessage(deleteMessageId)
        const data = await messagesApi.getMessages()
        if (data) {
          setMessages(data)
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      setDeleteMessageId(null)
    }
  }

  return (
    <>
      {deleteMessageId != null && (
        <ConfirmModal
          title="Dzēst"
          onClose={() => setDeleteMessageId(null)}
          onConfirm={deleteMessage}
          confirmText="Dzēst"
        >
          Vai tiešām vēlies dzēst?
        </ConfirmModal>
      )}
      {viewMessage != null && (
        <ViewMessageModal
          message={viewMessage}
          onClose={() => setViewMessage(null)}
        />
      )}
      <Container>
        {messages?.length === 0 ? (
          <EmptyScreen title="Šobrīd nav ziņojumu" />
        ) : (
          messages?.map?.((message) => (
            <MessageCard
              key={message.id}
              $isGrayOut={editMessageId != null && editMessageId !== message.id}
              $isActive={editMessageId === message.id}
            >
              <MessageHeader>
                <HeaderBlock>
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger>
                        <RecipientListButton
                          onClick={() => setViewMessage(message)}
                        >
                          {UserIcon}
                          {message.recipients?.length || 0} Darbinieki
                          {` (${
                            message.recipients?.filter(
                              (recipient) => !recipient.readAt,
                            ).length || 0
                          } nelasīti)`}
                        </RecipientListButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        Skatīt darbinieku sarakstu
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </HeaderBlock>
                <HeaderBlock>
                  <DateTime>{formatTime(message.createdAt)}</DateTime>
                  <MessageActions>
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger>
                          <IconButton onClick={() => setEditMessage(message)}>
                            {EditIcon}
                          </IconButton>
                        </TooltipTrigger>
                        <TooltipContent>Labot ziņojumu</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger>
                          <IconButton
                            onClick={() => setDeleteMessageId(message.id)}
                          >
                            {DeleteIcon}
                          </IconButton>
                        </TooltipTrigger>
                        <TooltipContent>Dzēst ziņojumu</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </MessageActions>
                  {/* <span>{message.fromUser?.name || "System"}</span> */}
                </HeaderBlock>
              </MessageHeader>
              <MessageContent
                content={message.content}
                attachments={message.attachments}
              />
            </MessageCard>
          ))
        )}
      </Container>
    </>
  )
}
